import { EnumTagSemanticoDTOTipo } from '@framework/api-client/api/users/schemas/enumTagSemanticoDTOTipo.ts';
import { type TagSemanticoDTO } from '@framework/api-client/api/users/schemas/tagSemanticoDTO.ts';
import {
  getGetTagSemanticiQueryOptions,
  getTagSemantici,
  useGetTagSemanticiId,
} from '@framework/api-client/api/users/tag-semantici-controller/tag-semantici-controller.ts';
import { type IWidgetConfigComponentProps } from '@framework/engine/schema/directives/IWidgetDirectiveType.ts';
import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import { useSectionType } from '@framework/engine/schema/sections/SectionTypeContext.tsx';
import { WidgetConfigurationSlotRender } from '@framework/engine/schema/widgets/repository/common/config-slots/WidgetConfigurationSlotRender.tsx';
import { WidgetConfigurationSlots } from '@framework/engine/schema/widgets/repository/common/config-slots/WidgetConfigurationSlots.tsx';
import { AsyncSelectInput } from '@framework/form/inputs/AsyncSelectInput.tsx';
import { CheckboxInput } from '@framework/form/inputs/CheckboxInput.tsx';
import { SwitchInput } from '@framework/form/inputs/SwitchInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import {
  CHECKBOX_EVIDENCE_CARD_LAYOUT,
  SWITCH_EVIDENCE_CARD_LAYOUT,
} from '@framework/form/item/formItemLayouts.ts';
import { SwitchEvidenceCard } from '@framework/ui/components/configuration-card/SwitchEvidenceCard.tsx';
import { WidgetConfigurationSubCard } from '@framework/ui/components/configuration-card/widget/WidgetConfigurationSubCard.tsx';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { StyledTag } from '@framework/ui/components/tag/StyledTag.tsx';
import { IconTag } from '@tabler/icons-react';
import { Flex } from 'antd';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { deriveSemanticTagMode } from '../../../semantic-tags/deriveSemanticTagMode.ts';
import { WidgetTypeMap } from '../../widgets/map/IMapWidget.ts';
import { SemanticTagBadge } from './components/semantic-tags/SemanticTagBadge.tsx';
import {
  SEMANTIC_TAG_MODE_INFO,
  SemanticTagModeCallout,
} from './components/semantic-tags/SemanticTagModeCallout.tsx';

/** Indenta la checkbox sotto lo switch (allineata al testo, non al toggle). */
const SwitchEvidenceCardIndent = styled.div`
  padding-left: 52px;
`;

function dataTypeToSemanticTagType(dataType: Datatype | null | undefined) {
  switch (dataType) {
    case Datatype.Boolean:
      return EnumTagSemanticoDTOTipo.BOOLEAN;
    case Datatype.Date:
      return EnumTagSemanticoDTOTipo.DATE;
    case Datatype.Number:
      return EnumTagSemanticoDTOTipo.NUMERIC;
    case Datatype.String:
      return EnumTagSemanticoDTOTipo.TEXT;
    case Datatype.File:
      return EnumTagSemanticoDTOTipo.FILE;
    // Il tipo Geometria viene calcolato direttamente dal widgetType
    // e non dal dataType del widget
    default:
      return null;
  }
}

function renderSemanticTagOption(option: any) {
  return <SemanticTagBadge semanticTag={option.data.object} />;
}

export interface IMetadataDirectiveConfigComponent
  extends IWidgetConfigComponentProps {}

/**
 * Sub-card "Significato Personalizzato": esporta il valore del campo come
 * metadato e lo associa a un tag semantico.
 */
export function MetadataDirectiveConfigComponent(
  props: IMetadataDirectiveConfigComponent,
) {
  const { widget, widgetType } = props;
  const isEnabled = useWatch({ name: `${props.name}.enabled` });
  const selectedSemanticTagId = useWatch({ name: `${props.name}.semanticTag` });

  const { sectionType } = useSectionType();

  const { data: selectedSemanticTag } = useGetTagSemanticiId(
    selectedSemanticTagId ?? '',
    { query: { enabled: !!selectedSemanticTagId } },
  );

  // Chip di riepilogo e spaziatura dipendono dalla modalità del tag
  // selezionato; il callout informativo è delegato a SemanticTagModeCallout.
  const tagValueMode = selectedSemanticTag
    ? deriveSemanticTagMode(
        selectedSemanticTag.flMultiplo,
        selectedSemanticTag.aggregazione,
      )
    : undefined;
  const tagValueModeInfo = tagValueMode
    ? SEMANTIC_TAG_MODE_INFO[tagValueMode]
    : null;

  const semanticTagType = useMemo(() => {
    // Per i campi dinamici non è possibile determinare il tipo di tag semantico
    // quindi li torniamo tutti
    if (widgetType.dynamicReference) return undefined;
    if (widgetType.type === WidgetTypeMap) {
      return EnumTagSemanticoDTOTipo.MAPPA;
    }
    return dataTypeToSemanticTagType(
      widgetType.getReference?.([], widget)?.field.type,
    );
  }, [widget, widgetType]);

  if (!widgetType) return null;

  // La direttiva metadata è disponibile solo per i widget di frontoffice e b
  // ackoffice, perché ha senso solo in quei contesti (non ha senso su document
  // o su widget standalone)
  if (sectionType !== 'FRONTOFFICE' && sectionType !== 'BACKOFFICE')
    return null;

  return (
    <WidgetConfigurationSlotRender
      slot={WidgetConfigurationSlots.Semantic}
      order={30}
    >
      <WidgetConfigurationSubCard
        icon={<TbIcon icon={IconTag} />}
        title="Significato Personalizzato"
        anchorTitle="significato-personalizzato"
        description={
          <>
            Permette di esportare il valore del campo come <b>metadato</b> e di
            associarlo a un <b>tag semantico</b>.
          </>
        }
        summary={
          isEnabled || tagValueModeInfo ? (
            <>
              {isEnabled && <StyledTag color="blue">Metadato</StyledTag>}
              {tagValueModeInfo && (
                <StyledTag color={tagValueModeInfo.badgeColor}>
                  {tagValueModeInfo.badge}
                </StyledTag>
              )}
            </>
          ) : undefined
        }
      >
        <SwitchEvidenceCard>
          <FormItem
            name={`${props.name}.enabled`}
            label="Esporta come metadato"
            layout={SWITCH_EVIDENCE_CARD_LAYOUT}
          >
            <SwitchInput name={`${props.name}.enabled`} align="start">
              <Flex vertical>
                <b>Esporta come metadato</b>
                <span>
                  Indica che il valore del campo deve essere esportato come
                  metadato nella tabella dedicata.
                </span>
              </Flex>
            </SwitchInput>
          </FormItem>
          <SwitchEvidenceCardIndent>
            <FormItem
              name={`${props.name}.searchable`}
              label="Includi nella ricerca estesa"
              help="Il valore di questo campo sarà indicizzato e reso ricercabile all'interno della Ricerca Estesa testuale."
              layout={CHECKBOX_EVIDENCE_CARD_LAYOUT}
            >
              <CheckboxInput
                name={`${props.name}.searchable`}
                defaultChecked={false}
              >
                Includi nella ricerca estesa
              </CheckboxInput>
            </FormItem>
          </SwitchEvidenceCardIndent>
        </SwitchEvidenceCard>

        <VerticalSpace size={16} />

        <FormItem
          name={`${props.name}.semanticTag`}
          label="Tag semantico"
          help="Se il campo è esportato come metadato, indica il tag semantico da associare al campo."
          layout={{
            removeHelpMargin: !tagValueModeInfo,
          }}
        >
          <AsyncSelectInput
            allowClear
            name={`${props.name}.semanticTag`}
            queryFn={getTagSemantici}
            initialParams={{
              ordinamenti: ['nome'],
              tipo: semanticTagType ?? undefined,
              // Permettiamo di esportare:
              // - i tag di FRONTOFFICE sia su FO (Richieste) che su BO (Attività)
              // - i tag di BACKOFFICE solo su BO (Attività)
              tipoSezione:
                sectionType === 'BACKOFFICE' ? undefined : sectionType,
            }}
            queryOptions={getGetTagSemanticiQueryOptions}
            searchByField="nome"
            asOption={(semanticTag: TagSemanticoDTO) => ({
              value: semanticTag.id!,
              label: semanticTag.titolo ?? semanticTag.nome ?? semanticTag.id!,
            })}
            placeholder="Tag semantico"
            optionRender={renderSemanticTagOption}
            filter={option => {
              // Se li sto ritornando tutti, rimuovo comunque i tag di tipo
              // geometry, mappa e file che sono dedicati ai widget specifici
              if (semanticTagType == null) {
                return (
                  option.tipo !== EnumTagSemanticoDTOTipo.GEOMETRY &&
                  option.tipo !== EnumTagSemanticoDTOTipo.MAPPA &&
                  option.tipo !== EnumTagSemanticoDTOTipo.FILE
                );
              }
              return true;
            }}
          />
        </FormItem>

        <SemanticTagModeCallout semanticTag={selectedSemanticTag} />
      </WidgetConfigurationSubCard>
    </WidgetConfigurationSlotRender>
  );
}
